import {
  useCanAccess,
  useGetIdentity,
  useListContext,
  useTranslate,
} from "ra-core";
import { Upload } from "lucide-react";
import { matchPath, useLocation } from "react-router";
import { CreateButton } from "@/components/admin/create-button";
import { CanCreateHere } from "../misc/fromBitrix";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { DataImportButton } from "../dataImport/DataImportButton";
import { CanExport } from "../misc/CanExport";
import { TopToolbar } from "../layout/TopToolbar";
import { AccountManagerInput } from "../sales/AccountManagerInput";
import { DealArchivedList } from "./DealArchivedList";
import { DealCreate } from "./DealCreate";
import { DealEdit } from "./DealEdit";
import { DealListContent } from "./DealListContent";
import { DealShow } from "./DealShow";
import { OnlyMineInput } from "./OnlyMineInput";
import { NoProjects } from "../projects/NoProjects";
import {
  ALL_PROJECTS,
  useCurrentDealPipeline,
  useCurrentProject,
} from "../projects/useCurrentProject";
import type { Deal, Project } from "../types";

const DealList = () => {
  const { identity } = useGetIdentity();
  const { canAccess: canAccessSalesList, isPending } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const { currentId, isPending: projectsPending } = useCurrentProject();

  if (!identity || projectsPending) return null;
  if (currentId == null) return <NoProjects />;

  // Search and «Ответственный», nothing else (owner's rule 04.11)
  const dealFilters = [
    <SearchInput source="q" alwaysOn />,
    ...(isPending
      ? []
      : [
          canAccessSalesList ? (
            <AccountManagerInput source="sales_id" alwaysOn />
          ) : (
            <OnlyMineInput source="sales_id" alwaysOn />
          ),
        ]),
  ];

  // the deals of the project chosen in the header (the owner may pick all)
  const projectFilter =
    currentId === ALL_PROJECTS ? {} : { project_id: currentId };

  return (
    <List
      perPage={currentId === ALL_PROJECTS ? 500 : 100}
      filter={{ "archived_at@is": null, ...projectFilter }}
      title={false}
      sort={{ field: "index", order: "DESC" }}
      filters={dealFilters}
      actions={<DealActions />}
      pagination={null}
    >
      <DealLayout />
    </List>
  );
};

const DealLayout = () => {
  const location = useLocation();
  const matchCreate = matchPath("/deals/create", location.pathname);
  const matchShow = matchPath("/deals/:id/show", location.pathname);
  const matchEdit = matchPath("/deals/:id", location.pathname);
  const { currentId, current, projects } = useCurrentProject();

  const { data, isPending } = useListContext<Deal>();

  if (isPending) return null;
  const deals = data ?? [];

  return (
    <div className="w-full">
      {currentId === ALL_PROJECTS ? (
        <div className="flex flex-col gap-10">
          {projects.map((project) => (
            <ProjectBoard
              key={project.id}
              project={project}
              deals={deals}
              showTitle
            />
          ))}
        </div>
      ) : current ? (
        <ProjectBoard project={current} deals={deals} />
      ) : null}
      <DealArchivedList />
      <DealCreate open={!!matchCreate} />
      <DealEdit open={!!matchEdit && !matchCreate} id={matchEdit?.params.id} />
      <DealShow open={!!matchShow} id={matchShow?.params.id} />
    </div>
  );
};

/**
 * The board of one project: its deal pipeline (tabs above the board when the
 * project has several) with the stages the owner set up for it.
 */
const ProjectBoard = ({
  project,
  deals,
  showTitle = false,
}: {
  project: Project;
  deals: Deal[];
  showTitle?: boolean;
}) => {
  const translate = useTranslate();
  const { pipelines, pipeline, stages, setPipeline } = useCurrentDealPipeline(
    project.id,
  );
  if (!pipeline) return null;
  const pipelineDeals = deals.filter(
    (deal) => String(deal.pipeline_id) === String(pipeline.id),
  );

  return (
    <section
      aria-label={project.name}
      data-project={project.id}
      className="flex flex-col gap-3"
    >
      {showTitle ? (
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <span
            className="size-2.5 rounded-full bg-muted-foreground"
            style={project.color ? { background: project.color } : undefined}
            aria-hidden
          />
          {project.name}
        </h2>
      ) : null}
      {pipelines.length > 1 ? (
        <nav
          aria-label={translate("crm.projects.pipelines_label")}
          className="flex gap-1 rounded-lg bg-muted p-1 w-fit max-w-full overflow-x-auto"
        >
          {pipelines.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPipeline(p.id)}
              aria-current={p.id === pipeline.id ? "page" : undefined}
              className={cn(
                "rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
                p.id === pipeline.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {p.name}
            </button>
          ))}
        </nav>
      ) : null}
      <DealListContent stages={stages} deals={pipelineDeals} />
    </section>
  );
};

// On a phone only «Новая сделка»: import and export are desktop work.
const DealActions = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const { currentId } = useCurrentProject();
  return (
    <TopToolbar className="flex-wrap">
      {isMobile ? null : (
        <CanExport>
          {currentId === ALL_PROJECTS ? (
            // deals are imported into one project: the one chosen in the header
            <Button variant="outline" disabled>
              <Upload />
              {translate("crm.projects.import_choose_project")}
            </Button>
          ) : (
            <CanCreateHere>
              <DataImportButton resource="deals" />
            </CanCreateHere>
          )}
          <ExportButton />
        </CanExport>
      )}
      <CanCreateHere>
        <CreateButton label="resources.deals.action.new" />
      </CanCreateHere>
    </TopToolbar>
  );
};

export default DealList;
